import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY, Observable, Subject, fromEvent, merge, of, timer } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

const POLL_INTERVAL_MS = 60_000;

/** GET /player-auction/admin/contract-balance */
export interface ContractBalanceApi {
  orders: {
    open: number;
    pending: number;
    transferring: number;
    stopped: number;
    in_pool: number;
    in_pool_left_coins: number;
    outside_pool_left_coins: number;
  };
  buy_contracts: {
    active: number;
    recent_bought_coins: number;
    recent_bought_cards: number;
    window_hours: number;
  };
  stocks: {
    ready: number;
    ready_spendable_coins: number;
    transferring: number;
  };
  auto_buy: {
    configured: boolean;
    is_active: boolean;
    desired_stocks: number;
    active_leases: number;
  };
  /** Admin-set open orders (Pending + Transferring) to aim for per active buy contract. */
  target_ratio: number;
  generated_at: string;
}

/**
 * - add: fewer open orders than the target ratio asks for
 * - on-target: open orders match the target exactly
 * - hold: more open orders than the target ratio asks for
 * - no-buyers: orders are open but no buy contract is active to fill them
 * - empty: no open orders and no active buy contracts
 */
export type BalanceState = 'add' | 'on-target' | 'hold' | 'no-buyers' | 'empty';

export interface ContractBalance {
  /** Pending + Transferring orders: the ones the ratio counts. */
  activeOrders: number;
  pendingOrders: number;
  transferringOrders: number;
  /** Stopped orders are open but not counted against the ratio. */
  stoppedOrders: number;

  activeBuyContracts: number;
  /** Admin-set open orders to aim for per active buy contract. */
  targetRatio: number;
  /** activeBuyContracts × targetRatio, rounded to whole orders. */
  targetOrders: number;
  /** activeOrders / activeBuyContracts, null when no buy contract is active. */
  currentRatio: number | null;
  /** targetOrders - activeOrders: positive is how many orders to add, negative how many over. */
  gapOrders: number;

  readyStocks: number;
  autoBuy: ContractBalanceApi['auto_buy'];

  state: BalanceState;
  updatedAt: Date;
}

/**
 * Open orders vs. active buy contracts, which admins watch to decide whether to add orders. One
 * shared poll per tab: every widget subscribes to the same stream, polling
 * pauses while the tab is hidden and stops once no widget is mounted.
 */
@Injectable({ providedIn: 'root' })
export class ContractBalanceService {
  private http = inject(HttpClient);
  private url = environment.apiBaseURL + '/player-auction/admin/contract-balance';
  private refresh$ = new Subject<void>();

  readonly balance$: Observable<ContractBalance | null> = merge(
    fromEvent(document, 'visibilitychange').pipe(
      startWith(null),
      map(() => !document.hidden),
      distinctUntilChanged(),
      switchMap(visible => (visible ? timer(0, POLL_INTERVAL_MS) : EMPTY)),
    ),
    this.refresh$,
  ).pipe(
    // switchMap drops a slow response that a newer tick has already superseded.
    switchMap(() =>
      this.http.get<{ result: ContractBalanceApi }>(this.url).pipe(
        map(r => computeBalance(r.result)),
        catchError(() => of(null)),
      ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  refresh(): void {
    this.refresh$.next();
  }
}

export function computeBalance(api: ContractBalanceApi): ContractBalance {
  const activeOrders = api.orders.pending + api.orders.transferring;
  const activeBuyContracts = api.buy_contracts.active;
  const targetRatio = api.target_ratio;
  const targetOrders = Math.round(activeBuyContracts * targetRatio);
  const gapOrders = targetOrders - activeOrders;

  let state: BalanceState;
  if (activeOrders === 0 && activeBuyContracts === 0) state = 'empty';
  else if (activeBuyContracts === 0) state = 'no-buyers';
  else if (gapOrders > 0) state = 'add';
  else if (gapOrders < 0) state = 'hold';
  else state = 'on-target';

  return {
    activeOrders,
    pendingOrders: api.orders.pending,
    transferringOrders: api.orders.transferring,
    stoppedOrders: api.orders.stopped,
    activeBuyContracts,
    targetRatio,
    targetOrders,
    currentRatio: activeBuyContracts > 0 ? activeOrders / activeBuyContracts : null,
    gapOrders,
    readyStocks: api.stocks.ready,
    autoBuy: api.auto_buy,
    state,
    updatedAt: new Date(),
  };
}
